import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import DashboardLayout from './DashboardLayout';
import { getUsers } from '../firebase/userService';
import './UserProfile.css';

function UserProfile() {
  const { currentUser, updatePassword } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loadingUserData, setLoadingUserData] = useState(true);

  useEffect(() => {
    const loadUserData = async () => {
      if (currentUser?.email) {
        const usersResult = await getUsers();
        if (usersResult.success) {
          const user = usersResult.users.find(u => u.email?.toLowerCase() === currentUser.email.toLowerCase());
          setUserData(user);
        }
      }
      setLoadingUserData(false);
    };

    loadUserData();
  }, [currentUser]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    if (newPassword === currentPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);

    try {
      const result = await updatePassword(currentPassword, newPassword);
      if (result.success) {
        setSuccess('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(result.error || 'Failed to change password. Please check your current password.');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    }

    setLoading(false);
  };

  if (!currentUser) {
    navigate('/');
    return null;
  }

  return (
    <DashboardLayout>
      <div className="user-profile-container">
        <div className="profile-header">
          <button onClick={() => navigate(-1)} className="back-button">
            <ArrowLeft size={18} />
            <span>Back</span>
          </button>
          <h1>User Profile</h1>
          <p className="subtitle">Manage your account settings</p>
        </div>

        <div className="profile-content">
          {/* User Info Section */}
          <div className="profile-section">
            <h2>Account Information</h2>
            {loadingUserData ? (
              <p>Loading user data...</p>
            ) : (
              <div className="info-grid">
                <div className="info-item">
                  <label>Name</label>
                  <p>{userData?.name || 'Not set'}</p>
                </div>
                <div className="info-item">
                  <label>Email</label>
                  <p>{currentUser.email}</p>
                </div>
                <div className="info-item">
                  <label>Initials</label>
                  <p>{userData?.initials || 'N/A'}</p>
                </div>
                <div className="info-item">
                  <label>Role</label>
                  <p>{userData?.role || 'User'}</p>
                </div>
                {userData?.permissions && (
                  <>
                    <div className="info-item">
                      <label>Session Host</label>
                      <p>{userData.permissions.sessionHost ? 'Yes' : 'No'}</p>
                    </div>
                    <div className="info-item">
                      <label>Point Tracking</label>
                      <p>{userData.permissions.pointTracking ? 'Yes' : 'No'}</p>
                    </div>
                  </>
                )}
              </div>
            )}
            <p className="info-note">
              <strong>Note:</strong> To change your role or permissions, please contact an administrator.
            </p>
          </div>

          {/* Change Password Section */}
          <div className="profile-section">
            <h2>Change Password</h2>
            <p className="section-description">
              Update your password to keep your account secure. Make sure to use a strong password.
            </p>

            <form onSubmit={handleChangePassword} className="password-form">
              {error && (
                <div className="alert alert-error">
                  {error}
                </div>
              )}

              {success && (
                <div className="alert alert-success">
                  {success}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter your current password"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                className="change-password-btn"
                disabled={loading}
              >
                {loading ? 'Changing Password...' : 'Change Password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default UserProfile;
